<template>
    <div class="space-y-6">
        <div>
            <span class="font-medium text-gray-600">{{ t('codeEnter') }}</span>
            <div class="mt-1 flex items-center">
                <template v-if="phone !== undefined && phone !== ''">
                    <i class="pi pi-phone text-gray-600"></i>
                    <span class="text-900 ml-2 font-bold">{{ phone }}</span>
                </template>
                <template v-else>
                    <i class="pi pi-envelope text-gray-600"></i>
                    <span class="text-900 ml-2 font-bold">{{ email }}</span>
                </template>
            </div>
        </div>
        <div ref="codeRef">
            <input-text
                :model-value="code"
                placeholder="XXXXXX"
                inputmode="numeric"
                autocomplete="one-time-code"
                class="!text-[16px]"
                :disabled="isPending"
                @update:model-value="emit('update:code', $event)"
                @keydown.enter="verifyCode"
            />
            <div
                v-if="incorrectCodeMessage !== undefined"
                class="text-red-600 font-medium"
            >
                {{ incorrectCodeMessage }}
            </div>
        </div>
        <div class="flex w-full flex-col gap-2 sm:flex-row">
            <prime-button
                :label="t('goBack')"
                class="flex-1"
                icon="pi pi-arrow-left"
                outlined
                @click="cancel"
            />
            <prime-button
                :label="t('codeVerify')"
                class="flex-1"
                :loading="isPending"
                @click="verifyCode"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watchEffect } from 'vue'
import PrimeButton from 'primevue/button'
import InputText from 'primevue/inputtext'
import { useRpQuery, useSetViewer, useShowAlert } from '#imports'
import { useMutation } from '@tanstack/vue-query'
import { graphql } from '~/resources/graphql'
import type { VerifyInput } from '~/resources/graphql/graphql'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
    code: string
    token: string
    phone: string | undefined
    email: string | undefined
}>()

const emit = defineEmits<{
    (e: 'update:code', payload: string): void
    (e: 'done'): void
    (e: 'cancel'): void
    (e: 'collectName', payload: { id: string; homeFacilityId: string }): void
    (e: 'collectDateOfBirth', payload: string): void
}>()

const { t } = useI18n()
const codeRef = ref<HTMLDivElement>()

function selectCodeInput() {
    setTimeout(() => {
        codeRef.value?.querySelector('input')?.select?.()
    }, 50)
}

const query = useRpQuery({ orgLevel: true })
const { mutate, isPending } = useMutation({
    mutationFn: (input: VerifyInput) =>
        query(
            graphql(/** @lang GraphQL */ `
                mutation Verify($input: VerifyInput!) {
                    verify(input: $input) {
                        __typename
                        ... on VerifyResult {
                            record {
                                ...ViewerFragment
                                primaryCustomer {
                                    id
                                    firstName
                                    lastName
                                    homeFacility {
                                        id
                                    }
                                }
                            }
                        }
                        ... on VerifyDateOfBirthChallengeResult {
                            token
                        }
                        ... on IncorrectCodeProblem {
                            message
                        }
                        ... on RateLimitProblem {
                            message
                        }
                        ... on ValidationError {
                            fieldErrors {
                                path
                                message
                            }
                        }
                    }
                }
            `),
            {
                input
            }
        )
})

const attemptedCode = ref(false)
const showAlert = useShowAlert()
const incorrectCodeMessage = ref()
const setViewer = useSetViewer()
function verifyCode() {
    attemptedCode.value = true
    if (isPending.value) {
        return false
    }

    if (props.code === '') {
        incorrectCodeMessage.value = t('codeInvalid')
        selectCodeInput()
        return
    }

    // TODO: Add warning on invalid data
    mutate(
        {
            token: props.token,
            code: props.code
        },
        {
            onSuccess: data => {
                if (data.verify.__typename === 'VerifyResult') {
                    setViewer(data.verify.record)
                    // If empty customer, try to fill in the name now.
                    if (
                        data.verify.record.primaryCustomer.firstName === '' &&
                        data.verify.record.primaryCustomer.lastName === ''
                    ) {
                        emit('collectName', {
                            id: data.verify.record.primaryCustomer.id,
                            homeFacilityId:
                                data.verify.record.primaryCustomer.homeFacility
                                    .id
                        })
                    }
                    // Otherwise we are just done
                    else {
                        emit('done')
                    }
                }
                // If birthday challenge switch to that
                else if (
                    data.verify.__typename ===
                    'VerifyDateOfBirthChallengeResult'
                ) {
                    emit('collectDateOfBirth', data.verify.token)
                } else if (data.verify.__typename === 'RateLimitProblem') {
                    incorrectCodeMessage.value = data.verify.message
                    selectCodeInput()
                } else if (data.verify.__typename === 'IncorrectCodeProblem') {
                    incorrectCodeMessage.value = t('codeInvalid')
                    selectCodeInput()
                } else if (data.verify.__typename === 'ValidationError') {
                    incorrectCodeMessage.value = t('codeInvalid')
                    selectCodeInput()
                } else {
                    showAlert(t('oops'), t('somethingWrongWarning'))
                }
            }
        }
    )
}

function cancel() {
    emit('cancel')
}

onMounted(() => {
    codeRef.value?.querySelector('input')?.focus?.()
})

// Only perform the auto-submit on the first go
watchEffect(() => {
    if (attemptedCode.value === false && /^\d{6}$/.test(props.code)) {
        verifyCode()
    }
})
</script>
